import { Link } from "react-router-dom";
import { ArrowRight, Network as NetworkIcon, Users, Award, Play, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";

// Collaborative network — sorted alphabetically by name
const NETWORK_PARTNERS = [
  {
    name: "Australian Computer Society",
    short: "ACS",
    logo: "/network/acs.png",
    url: "https://www.acs.org.au/",
  },
  {
    name: "Australian Government — Department of Industry, Science and Resources",
    short: "DISR",
    logo: "/network/aus-gov-industry-science-resources.png",
    url: "https://www.industry.gov.au/",
  },
  {
    name: "Boston University",
    short: "BU",
    logo: "/network/boston-university.png",
    url: "https://www.bu.edu/",
  },
  {
    name: "Global AI Ethics Institute",
    short: "GAIEI",
    logo: "/network/gaiei.png",
    url: "https://globalethics.ai/",
  },
  {
    name: "g.tec medical engineering — Brain-Computer Interfaces",
    short: "g.tec",
    logo: "/network/gtec.svg",
    url: "https://www.gtec.at/",
  },
  {
    name: "Harvard Business Review",
    short: "HBR",
    logo: "/network/harvard-business-review.png",
    url: "https://hbr.org/",
  },
  {
    name: "iFusion",
    short: "iFusion",
    logo: "/network/ifusion.png",
    url: "https://ifusion.com.au/",
  },
  {
    name: "IEEE — Advancing Technology for Humanity",
    short: "IEEE",
    logo: "/network/ieee.png",
    url: "https://www.ieee.org/",
  },
  {
    name: "International Federation of Red Cross and Red Crescent Societies",
    short: "IFRC",
    logo: "/network/ifrc.png",
    url: "https://www.ifrc.org/",
  },
  {
    name: "META61",
    short: "META61",
    logo: "/network/meta61.png",
    url: "https://meta61.com/",
  },
  {
    name: "OECD.AI — Policy Observatory",
    short: "OECD.AI",
    logo: "/network/oecd-ai.png",
    url: "https://oecd.ai/",
  },
  {
    name: "The Fair — Festival of AI + Robotics",
    short: "The Fair",
    logo: "/network/the-fair.png",
    url: "https://thefair.com.au/",
  },
  {
    name: "The Teachers' Guild of NSW",
    short: "Teachers Guild NSW",
    logo: "/network/teachers-guild-nsw.png",
    url: "https://www.teachersguild.nsw.edu.au/",
  },
  {
    name: "The University of Melbourne",
    short: "Unimelb",
    logo: "/network/university-of-melbourne.png",
    url: "https://www.unimelb.edu.au/",
  },
  {
    name: "The University of Sydney",
    short: "USyd",
    logo: "/network/university-of-sydney.png",
    url: "https://www.sydney.edu.au/",
  },
  {
    name: "United Nations — Sustainable Development Goals",
    short: "UN SDGs",
    logo: "/network/un-sdg.png",
    url: "https://sdgs.un.org/goals",
  },
  {
    name: "UNSW Sydney",
    short: "UNSW",
    logo: "/network/unsw.png",
    url: "https://www.unsw.edu.au/",
  },
  {
    name: "University of Technology Sydney",
    short: "UTS",
    logo: "/network/uts.png",
    url: "https://www.uts.edu.au/",
  },
  {
    name: "Western Sydney University",
    short: "WSU",
    logo: "/network/western-sydney-university.png",
    url: "https://www.westernsydney.edu.au/",
  },
  {
    name: "Women in AI",
    short: "Women in AI",
    logo: "/network/women-in-ai.png",
    url: "https://www.womeninai.co/",
  },
  {
    name: "WWF — World Wide Fund for Nature",
    short: "WWF",
    logo: "/network/wwf.png",
    url: "https://www.worldwildlife.org/",
  },
];

export const NetworkPage = () => {
  return (
    <div id="main-content" className="pt-20" data-testid="network-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950" />
        <div
          className="absolute inset-0 opacity-50"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 25% 40%, rgba(59,130,246,0.18), transparent 60%), radial-gradient(ellipse 60% 50% at 80% 70%, rgba(139,92,246,0.12), transparent 60%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="overline text-blue-300 mb-4">Network</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight mb-6">
              Our Collaborative Network
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 leading-relaxed mb-5">
              Our collaborative network reflects the strength of the Centre's interdisciplinary mission. By working across research, technology, ethics, and public-interest innovation, we build connections that support deeper inquiry into consciousness and its relevance to science, intelligence, and society.
            </p>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              These collaborations help us extend the Centre's reach, strengthen its research environment, and contribute to a broader international conversation. This is an expanding network, and we continue to welcome new forms of engagement that align with our strategic vision and mission.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Affiliations — section intro */}
      <section className="pt-16 lg:pt-24 pb-6 bg-slate-50" data-testid="featured-affiliations-intro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <p className="overline text-brand-primary mb-4">Featured Affiliations</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
              Active Memberships and Hosting Roles
            </h2>
            <p className="text-slate-600 leading-relaxed">
              The Centre holds active formal positions in two of the most significant international platforms in neurotechnology and brain–computer interfaces — anchoring our wider network in concrete commitments to research, education, and standards.
            </p>
          </div>
        </div>
      </section>

      {/* IEEE Brain Technical Community — Membership */}
      <section className="pt-10 pb-16 lg:pt-12 lg:pb-24 bg-slate-50" data-testid="ieee-brain-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-10">
            <p className="overline text-brand-primary mb-4">Membership &amp; Affiliation · 1 of 2</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
              Member of the IEEE Brain Technical Community
            </h2>
          </div>

          <div className="grid lg:grid-cols-[auto,1fr] gap-8 lg:gap-12 items-start max-w-5xl mx-auto mb-12">
            <a
              href="https://brain.ieee.org/"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white rounded-2xl p-8 lg:p-10 ring-1 ring-slate-200 shadow-sm hover:shadow-md transition-shadow w-full lg:w-72 flex-shrink-0"
              data-testid="ieee-brain-logo-link"
              aria-label="Visit IEEE Brain Technical Community"
            >
              <img
                src="/network/ieee-brain.jpg"
                alt="IEEE Brain Technical Community"
                className="w-full h-auto"
                loading="lazy"
              />
            </a>

            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center flex-shrink-0">
                  <Award className="w-5 h-5 text-pink-600" />
                </div>
                <p className="overline text-brand-primary">Affiliation</p>
              </div>
              <p className="text-slate-700 leading-relaxed mb-4">
                As an IEEE-wide effort, the IEEE Brain Technical Community unites engineering and computing expertise across IEEE Societies and Councils relevant to neuroscience, and provides an avenue for IEEE to work with multiple constituencies in academia, industry, and government to incubate and sponsor new activities, projects, and standards that facilitate bringing neurotechnology to market in an ethical and responsible manner.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                Formed in 2015 as the IEEE Brain Initiative, it transitioned to the IEEE Brain Technical Community in 2021. Its goal is to facilitate cross-disciplinary collaboration and coordination to advance research, standardization, and development of engineering and technology to improve understanding of the brain, in order to treat diseases and improve the human condition.
              </p>
              <p className="text-slate-700 leading-relaxed mb-6">
                IEEE Brain is a member of The BRAIN Initiative Alliance and the American Brain Coalition, working to further connect IEEE members with other organizations and agencies that foster global efforts to share knowledge and discoveries in neuroscience and neural engineering.
              </p>
              <Button
                asChild
                className="bg-slate-900 text-white hover:bg-slate-800"
                data-testid="ieee-brain-visit-btn"
              >
                <a
                  href="https://brain.ieee.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit IEEE Brain <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </div>
          </div>

          {/* YouTube intro */}
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-pink-100 flex items-center justify-center">
                <Play className="w-5 h-5 text-pink-600" />
              </div>
              <div>
                <p className="overline text-brand-primary">Introduction</p>
                <h3 className="font-serif text-xl text-slate-900 leading-snug">
                  An introduction to the IEEE Brain Technical Community
                </h3>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg ring-1 ring-slate-200 aspect-video bg-slate-900">
              <iframe
                src="https://www.youtube.com/embed/-0zHmrSksxs"
                title="An introduction to the IEEE Brain Technical Community"
                className="w-full h-full"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="ieee-brain-video-embed"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Spring School — Official Host */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="spring-school-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-10">
            <p className="overline text-brand-primary mb-4">Official Host · 2 of 2</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
              BCI &amp; Neurotechnology Spring School 2026
            </h2>
            <p className="text-slate-600 leading-relaxed">
              The Centre for Consciousness Studies is an Official Host of the BCI &amp; Neurotechnology Spring School.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center max-w-6xl mx-auto">
            {/* Official-host promo image */}
            <a
              href="https://www.gtec.at/spring-school-2026/"
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl overflow-hidden shadow-lg ring-1 ring-slate-200 group"
              data-testid="spring-school-image-link"
              aria-label="Visit BCI & Neurotechnology Spring School 2026"
            >
              <img
                src="/network/spring-school-official-host.jpg"
                alt="Centre for Consciousness Studies — Official Host of the BCI & Neurotechnology Spring School 2026, April 20–29, 2026"
                className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-500"
                loading="lazy"
              />
            </a>

            {/* Description + CTA */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <p className="overline text-brand-primary">April 20–29, 2026</p>
              </div>
              <p className="text-slate-700 leading-relaxed mb-4">
                The BCI &amp; Neurotechnology Spring School is a rigorous, globally connected education program in brain–computer interfaces and neurotechnology. It brings together expert-led lectures, live demonstrations, and interactive discussions spanning research, clinical practice, and industry applications.
              </p>
              <p className="text-slate-700 leading-relaxed mb-4">
                Through this program, participants from around the world join a shared learning experience that connects local academic communities with a wider international network. The Spring School offers several ways for universities, companies, and research institutions to get involved: as individual online attendees, official hosts, live viewing hubs, credit-granting universities, and hackathon hosts through BR41N.IO.
              </p>
              <p className="text-slate-700 leading-relaxed mb-6">
                This model combines global access with strong local engagement, creating opportunities for collaboration, academic recognition, and practical innovation. Free of charge and supported by certificates, local credit pathways, and hands-on activities, the Spring School is more than an online program — it is a global academic movement helping shape the future of neurotechnology.
              </p>
              <Button
                asChild
                className="bg-slate-900 text-white hover:bg-slate-800"
                data-testid="spring-school-visit-btn"
              >
                <a
                  href="https://www.gtec.at/spring-school-2026/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit the Spring School <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Logo Wall */}
      <section className="py-16 lg:py-24 bg-white" data-testid="network-partners">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12 lg:mb-16">
            <p className="overline text-brand-primary mb-4">Wider Network</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
              Our Broader Collaborative Network
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Beyond our active memberships and hosting roles, the Centre's work intersects with a wider network of universities, standards bodies, government agencies, industry partners, and public-interest organisations — supporting interdisciplinary research, ethical innovation, and policy engagement worldwide.
            </p>
          </div>

          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6"
            data-testid="network-logo-grid"
          >
            {NETWORK_PARTNERS.map((p) => (
              <a
                key={p.short}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white border-2 border-slate-100 rounded-xl p-5 lg:p-6 flex flex-col items-center justify-center gap-4 transition-all duration-300 hover:border-brand-highlight hover:shadow-lg hover:-translate-y-0.5"
                data-testid={`network-partner-${p.short.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                aria-label={`Visit ${p.name}`}
              >
                <div className="h-20 lg:h-24 w-full flex items-center justify-center">
                  <img
                    src={p.logo}
                    alt={`${p.name} logo`}
                    className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <p className="text-xs lg:text-sm text-slate-700 text-center font-medium leading-snug">
                  {p.name}
                </p>
              </a>
            ))}

            {/* "Coming soon" placeholder tile */}
            <div
              className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl p-5 lg:p-6 flex flex-col items-center justify-center gap-4 text-center"
              data-testid="network-coming-soon-tile"
            >
              <div className="h-20 lg:h-24 w-full flex items-center justify-center">
                <div className="w-14 h-14 rounded-full bg-white ring-1 ring-slate-200 flex items-center justify-center">
                  <NetworkIcon className="w-7 h-7 text-slate-400" />
                </div>
              </div>
              <p className="text-xs lg:text-sm text-slate-500 font-medium leading-snug">
                More collaborators coming soon
              </p>
            </div>
          </div>

          <p className="text-sm text-slate-500 text-center mt-8 max-w-2xl mx-auto" data-testid="network-expanding-note">
            This is an expanding network. We are continuing to finalise additional collaborators, and further partner logos will be added here over time.
          </p>
        </div>
      </section>

      {/* Cross-link to Opportunities */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="network-opportunities-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="grid sm:grid-cols-[auto,1fr] gap-6 sm:gap-8 items-center p-8 lg:p-10">
              <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Users className="w-8 h-8 lg:w-10 lg:h-10 text-blue-600" />
              </div>
              <div>
                <p className="overline text-brand-primary mb-3">Get Involved</p>
                <h3 className="font-serif text-2xl lg:text-3xl text-slate-900 mb-4 leading-snug">
                  Explore opportunities to collaborate with us
                </h3>
                <p className="text-slate-700 leading-relaxed mb-6">
                  To learn more about how you can work with us, please visit our Opportunities page. It outlines the kinds of engagement and collaboration we welcome, including opportunities for dialogue, shared inquiry, and cross-institutional connection in line with the Centre's wider vision and mission.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    asChild
                    size="lg"
                    className="bg-slate-900 text-white hover:bg-slate-800"
                    data-testid="network-opportunities-btn"
                  >
                    <Link to="/opportunities">
                      Explore Opportunities <ArrowRight className="ml-2 w-4 h-4" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    size="lg"
                    className="border-slate-300"
                    data-testid="network-contact-btn"
                  >
                    <Link to="/contact">Contact Us</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NetworkPage;
